/* eslint-disable react-hooks/exhaustive-deps */
import {
    Button,
    Col,
    Container,
    Form,
    Image,
    Row,
    Spinner,
    Alert
} from "react-bootstrap";
import Navbar from "../../components/navbar";
import Layout from "../../layout";
import { useBetween } from "use-between";

import "./index.scss";
import { useEffect, useState } from "react";
import useApi from "../useApi";
import { formatPhoneNumber } from "../../plugins";

const Details = () => {
    const {
        updating,
        customer,
        customerId,
        getCustomer,
        setUpdating,
        updateCustomer,
        logout
    } = useBetween(useApi);

    const [formData, setFormData] = useState({
        businessName: "",
        email: "",
        address: "",
        licenseNumber: "",
        phone: "",
        dispensaryAddress: "",
        dispensaryCity: "",
        dispensaryState: "",
        dispensaryZip: ""
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [phoneError, setPhoneError] = useState("");

    useEffect(() => {
        setUpdating(true);
        getCustomer(customerId)
            .then(() => setUpdating(false))
            .catch(() => logout());
    }, [customerId]);

    useEffect(() => {
        if (Object.keys(customer).length) {
            const rawPhone = customer.Phone1 || "";
            const formattedPhone = formatPhoneNumber(rawPhone);

            setFormData({
                businessName: customer["CustomerName"] || "",
                email: customer["Email"] || "",
                licenseNumber: customer["LIC#1: Primary License"] || "",
                phone: formattedPhone,
                dispensaryAddress: customer.AddressLine1 || "",
                dispensaryCity: customer.City || "",
                dispensaryState: customer.State || "",
                dispensaryZip: customer.PostalCode || ""
            });
        }
    }, [customer]);

    // Phone number formatting and validation
    const formatPhoneNumber = (value) => {
        if (!value) return '';

        // Remove all non-numeric characters
        const phoneNumber = value.replace(/\D/g, '');

        // If starts with 1, keep it (US country code)
        if (phoneNumber.startsWith('1') && phoneNumber.length <= 11) {
            const countryCode = phoneNumber.slice(0, 1);
            const areaCode = phoneNumber.slice(1, 4);
            const firstPart = phoneNumber.slice(4, 7);
            const secondPart = phoneNumber.slice(7, 11);

            if (phoneNumber.length <= 1) return `+${countryCode}`;
            if (phoneNumber.length <= 4) return `+${countryCode} (${areaCode}`;
            if (phoneNumber.length <= 7) return `+${countryCode} (${areaCode}) ${firstPart}`;
            return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
        }

        // If doesn't start with 1, assume US number and add country code
        if (phoneNumber.length <= 10) {
            const areaCode = phoneNumber.slice(0, 3);
            const firstPart = phoneNumber.slice(3, 6);
            const secondPart = phoneNumber.slice(6, 10);

            if (phoneNumber.length <= 3) return phoneNumber.length > 0 ? `+1 (${areaCode}` : '';
            if (phoneNumber.length <= 6) return `+1 (${areaCode}) ${firstPart}`;
            return `+1 (${areaCode}) ${firstPart}-${secondPart}`;
        }

        return value; // Return as is if longer than expected
    };

    const validatePhoneNumber = (phoneNumber) => {
        // Remove all non-numeric characters for validation
        const digitsOnly = phoneNumber.replace(/\D/g, '');

        // Check if it's exactly 10 digits (US number) or 11 digits (with country code 1)
        if (digitsOnly.length === 10) {
            return true; // Valid US number
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
            return true; // Valid US number with country code
        }

        return false;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            const formattedPhone = formatPhoneNumber(value);
            const digitsOnly = value.replace(/\D/g, '');

            // Prevent input if more than 11 digits
            if (digitsOnly.length > 11) {
                return;
            }

            setFormData(prev => ({
                ...prev,
                [name]: formattedPhone
            }));

            // Validate phone number
            if (formattedPhone && !validatePhoneNumber(formattedPhone)) {
                setPhoneError("Please enter a valid 10-digit US phone number");
            } else {
                setPhoneError("");
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };


    useEffect(() => {
    }, [formData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setShowErrorAlert(false);
        setShowSuccessAlert(false);

        // Validate phone number before submission
        if (formData.phone && !validatePhoneNumber(formData.phone)) {
            setPhoneError("Please enter a valid 10-digit US phone number");
            setIsSubmitting(false);
            return;
        }

        try {
            // Clean phone number for API submission (remove formatting)
            const cleanedPhone = formData.phone.replace(/\D/g, '');
            const submissionData = {
                ...formData,
                phone: cleanedPhone
            };

            await updateCustomer(customerId, submissionData);
            setShowSuccessAlert(true);

            // Refresh customer data and reformat phone number
            await getCustomer(customerId);

        } catch (error) {
            setShowErrorAlert(true);
            setErrorMessage(error?.message || "Failed to update profile. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className='bg-gray'>
                <Container className='details-container d-flex'>
                    <Navbar active='details' />
                    {Object.keys(customer).length ? (
                        <div className='details-wrapper'>
                            {showSuccessAlert && (
                                <Alert
                                    variant="success"
                                    dismissible
                                    onClose={() => setShowSuccessAlert(false)}
                                    className="mb-3"
                                >
                                    Profile updated successfully!
                                </Alert>
                            )}

                            {showErrorAlert && (
                                <Alert
                                    variant="danger"
                                    dismissible
                                    onClose={() => setShowErrorAlert(false)}
                                    className="mb-3"
                                >
                                    {errorMessage}
                                </Alert>
                            )}
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className='form-group'>
                                            <Form.Label>
                                                Email
                                            </Form.Label>
                                            <Form.Control
                                                type='email'
                                                name='email'
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder='Email Address'
                                                required
                                                className="editable-field"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className='form-group'>
                                            <Form.Label>
                                                Phone
                                            </Form.Label>
                                            <Form.Control
                                                type='tel'
                                                name='phone'
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder='+1 (555) 123-4567'
                                                required
                                                className={`editable-field ${phoneError ? 'is-invalid' : ''}`}
                                                maxLength={17} // +1 (555) 123-4567
                                            />
                                            {phoneError && (
                                                <div className="invalid-feedback d-block">
                                                    {phoneError}
                                                </div>
                                            )}
                                            <Form.Text className="text-muted">
                                                Enter 10-digit US phone number
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className='form-group'>
                                            <Form.Label>
                                                Business Name
                                            </Form.Label>
                                            <Form.Control
                                                type='text'
                                                name='businessName'
                                                value={formData.businessName}
                                                onChange={handleInputChange}
                                                placeholder='Business Name'
                                                disabled
                                                className="disabled-field"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className='form-group'>
                                            <Form.Label>
                                                License Number
                                            </Form.Label>
                                            <Form.Control
                                                type='text'
                                                name='licenseNumber'
                                                value={formData.licenseNumber}
                                                onChange={handleInputChange}
                                                placeholder='License Number'
                                                disabled
                                                className="disabled-field"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className='form-group'>
                                            <Form.Label>
                                                Dispensary Address
                                            </Form.Label>
                                            <Form.Control
                                                type='text'
                                                name='dispensaryAddress'
                                                value={formData.dispensaryAddress}
                                                onChange={handleInputChange}
                                                placeholder='Dispensary Street Address'
                                                disabled
                                                className="disabled-field"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className='form-group'>
                                            <Form.Label>
                                                Dispensary City
                                            </Form.Label>
                                            <Form.Control
                                                type='text'
                                                name='dispensaryCity'
                                                value={formData.dispensaryCity}
                                                onChange={handleInputChange}
                                                placeholder='Dispensary City'
                                                disabled
                                                className="disabled-field"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className='form-group'>
                                            <Form.Label>
                                                Dispensary State
                                            </Form.Label>
                                            <Form.Control
                                                type='text'
                                                name='dispensaryState'
                                                value={formData.dispensaryState}
                                                onChange={handleInputChange}
                                                placeholder='Dispensary State'
                                                disabled
                                                className="disabled-field"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className='form-group'>
                                            <Form.Label>
                                                Dispensary ZIP
                                            </Form.Label>
                                            <Form.Control
                                                type='text'
                                                name='dispensaryZip'
                                                value={formData.dispensaryZip}
                                                onChange={handleInputChange}
                                                placeholder='Dispensary ZIP Code'
                                                disabled
                                                className="disabled-field"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12} className="text-center mt-4">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={isSubmitting}
                                            className="px-8"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Updating...
                                                </>
                                            ) : (
                                                'Update Profile'
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                    ) : null}
                </Container>
            </div >
            {
                updating ? (
                    <div className='page-loading' >
                        <Spinner animation='border' role='status'>
                            <span className='visually-hidden'>Loading...</span>
                        </Spinner>
                    </div>
                ) : null}
        </Layout >
    );
};

export default Details;
